import React from "react";
import type { ReactNode } from "react";

import { Flex } from "../../Flex";
import { Icon } from "../../Icon";
import type { IconProps } from "../../Icon";
import { Text } from "../../Text";

export interface EmptyStateProps {
  /** Short statement of the empty condition ("No data to display"). The
   * widget layer owns the copy (createMessage) — never hard-code here. */
  title: string;
  /** Optional supporting hint ("Connect a datasource to see rows."). */
  description?: string;
  /** Optional decorative icon (always aria-hidden). */
  icon?: string;
  /** Optional keyboard-reachable call to action; editor-mode only in
   * widget consumers — viewers must never see a dead-end control. */
  action?: ReactNode;
}

/**
 * First-class empty-state presentation for data regions (M3 async
 * contract). Renders only when the region is NOT loading — loading beats
 * empty; see the skeleton/empty-states design. Distinct from the ADS
 * EmptyState (packages/design-system/ads): this is the Anvil widget one.
 */
export function EmptyState(props: EmptyStateProps) {
  const { action, description, icon, title } = props;

  return (
    <Flex
      alignItems="center"
      data-testid="t--wds-empty-state"
      direction="column"
      gap="spacing-2"
      isInner
      justifyContent="center"
      padding="spacing-4"
    >
      {Boolean(icon) && (
        <Icon
          aria-hidden="true"
          name={icon as IconProps["name"]}
          size="large"
        />
      )}
      <Text color="neutral-subtle" textAlign="center">
        {title}
      </Text>
      {Boolean(description) && (
        <Text color="neutral-subtle" size="caption" textAlign="center">
          {description}
        </Text>
      )}
      {action}
    </Flex>
  );
}
